import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Settings } from 'lucide-react';
import { MicLevelMeter } from './MicLevelMeter';
import { AudioInputPicker } from './AudioInputPicker';
import { loadManifest, assetFor, type AvatarManifest, type AvatarRole, type RendererProfile } from '../lib/avatarManifest';
import { useVoice } from '../contexts/VoiceContext';
import './ArcturianPanel.css';

// Arcturian als quadratisches Feld unter der Sessionliste (Alex 2026-08-04:
// „unterhalb der Session-Liste ein quadratisches Feld reserviert, wo die
// Video-State-Machine ist, und wenn ich draufklicke, kann ich mit ihm reden").
//
// BEWUSST KEIN OVERLAY: Ein freigestellter Avatar rechts unten wäre die andere
// Variante gewesen, scheitert aber daran, dass die Arcturian-Clips (anders als
// die Jeppa-Schlucht-Videos) keinen keybaren Hintergrund haben — sie sind
// 960×960 mit gemaltem Kosmos. Ein fester quadratischer Rahmen zeigt genau das,
// wofür die Videos gebaut wurden, statt sie in eine Freistellung zu zwingen,
// die es nicht gibt.
//
// GRENZE: Dieses Panel ZEIGT nur. Es mintet keine Arcturian-Session, löst keine
// Action aus und rendert kein „ausgeführt". Die produktive Arcturian-Lane
// wartet auf den revisionsgebundenen Ownership-Eintrag in #4431; bis dahin
// führt der Klick in den bestehenden, freigegebenen Sprachmodus.

export type ArcturianState = 'idle' | 'listening' | 'thinking' | 'talking' | 'happy' | 'sad';

// Zustand → Manifest-Rolle. `talking` ist absichtlich auf talk_medium
// abgebildet: ohne bekannte Antwortlänge ist die mittlere Variante die, die in
// beide Richtungen am wenigsten falsch wirkt (talkRoleFor() wählt genauer,
// sobald der Player die Dauer kennt).
const ROLE: Record<ArcturianState, AvatarRole> = {
  idle: 'idle',
  listening: 'listening',
  thinking: 'thinking',
  talking: 'talk_medium',
  happy: 'happy',
  sad: 'sad',
};

interface Props {
  /** Aktueller Zustand — kommt später aus der Realtime-Session. */
  state?: ArcturianState;
  /** Gedrückthalten des Felds: Mikrofon öffnen; Loslassen: wieder schließen. */
  onTalkStart?: () => void;
  onTalkEnd?: () => void;
  /** True, während eine Sprachsitzung läuft (steuert Rahmen + Beschriftung). */
  live?: boolean;
  /** True nur während der Operator das PTT-Feld hält und Audio gesendet wird. */
  talking?: boolean;
  /** Nur außergewöhnliche Zustände als Overlay im Video. Der normale
   *  „tippen zum Sprechen"-Hinweis ist bewusst entfernt. */
  caption?: string;
  /** Start fehlgeschlagen — färbt die Zeile, damit es nicht wie ein
   *  Ruhezustand aussieht. Bewusst NICHT `failed`: so heißt hier schon der
   *  Ladefehler des Manifests, und zwei Bedeutungen unter einem Namen sind
   *  genau die Art Verwechslung, die später jemand teuer bezahlt. */
  startFailed?: boolean;
  /** Bedienleiste unter dem Video. Bewusst AUSSERHALB des Video-Knopfes —
   *  verschachtelte Buttons sind ungültiges HTML, und ein Klick auf „Stopp"
   *  darf nicht zugleich das Video-Element auslösen. */
  controls?: ReactNode;
}

export function ArcturianPanel({ state = 'idle', onTalkStart, onTalkEnd, live = false, talking = false, startFailed = false, caption, controls }: Props) {
  const [manifest, setManifest] = useState<AvatarManifest | null>(null);
  const [profile, setProfile] = useState<Required<RendererProfile> | null>(null);
  const [failed, setFailed] = useState('');
  // Doppelpuffer: das web-Profil erlaubt 2 Decoder im Übergang und 1 im
  // Ruhezustand. Zwei <video>-Elemente, von denen immer nur eines sichtbar ist
  // — der Wechsel ist ein Opacity-Crossfade, kein Neuladen im selben Element
  // (das würde schwarz blitzen).
  const [front, setFront] = useState(0);
  const videos = [useRef<HTMLVideoElement | null>(null), useRef<HTMLVideoElement | null>(null)];
  const currentRole = useRef<AvatarRole | null>(null);

  useEffect(() => {
    let alive = true;
    void loadManifest().then((r) => {
      if (!alive) return;
      if (r.ok) { setManifest(r.manifest); setProfile(r.profile); }
      else setFailed(r.reason);
    });
    return () => { alive = false; };
  }, []);

  const role = ROLE[state];

  useEffect(() => {
    if (!manifest || !profile) return;
    if (currentRole.current === role) return;
    const asset = assetFor(manifest, role);
    if (!asset?.url) return;

    const next = front === 0 ? 1 : 0;
    const el = videos[next].current;
    if (!el) return;
    el.src = asset.url;
    el.loop = asset.loop !== false;
    // Stummschaltung ist Pflicht, nicht Höflichkeit: die Clips sind tonlos
    // (audio_track_count 0), und ohne `muted` verweigert der Browser Autoplay.
    el.muted = true;
    const swap = () => { setFront(next); currentRole.current = role; };
    el.play().then(swap).catch(() => {
      // Autoplay verweigert (z.B. strenge Energiespar-Einstellung): Poster
      // stehen lassen statt endlos zu versuchen.
      swap();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, manifest, profile, front]);

  // Ohne gültiges Manifest wird NICHTS gezeigt — kein Platzhalter-Kasten, der
  // wie ein kaputter Avatar aussieht. Der Grund steht im title für die Diagnose.
  if (failed || !manifest || !profile) {
    return failed ? <div className="arc-panel-slim" title={`Avatar nicht geladen: ${failed}`} /> : null;
  }

  const poster = manifest.canonical_frame?.poster || manifest.canonical_frame?.url;

  return (
    <div className={`arc-wrap${live ? ' live' : ''}`}>
    <button
      type="button"
      className={`arc-panel${live ? ' live' : ''}${talking ? ' talking' : ''}`}
      onPointerDown={(event) => {
        if (!event.isPrimary) return;
        event.preventDefault();
        try { event.currentTarget.setPointerCapture(event.pointerId); } catch { /* unsupported */ }
        onTalkStart?.();
      }}
      onPointerUp={(event) => {
        if (!event.isPrimary) return;
        onTalkEnd?.();
        try { event.currentTarget.releasePointerCapture(event.pointerId); } catch { /* already released */ }
      }}
      onPointerCancel={() => onTalkEnd?.()}
      onLostPointerCapture={() => onTalkEnd?.()}
      onKeyDown={(event) => {
        if ((event.key === ' ' || event.key === 'Enter') && !event.repeat) {
          event.preventDefault();
          onTalkStart?.();
        }
      }}
      onKeyUp={(event) => {
        if (event.key === ' ' || event.key === 'Enter') {
          event.preventDefault();
          onTalkEnd?.();
        }
      }}
      onBlur={() => onTalkEnd?.()}
      onContextMenu={(event) => event.preventDefault()}
      title="Gedrückt halten zum Sprechen"
      aria-label="Gedrückt halten, um mit Arcturian zu sprechen"
      aria-pressed={talking}
    >
      <span className="arc-stage">
        {[0, 1].map((i) => (
          <video
            key={i}
            ref={videos[i]}
            className={`arc-video${front === i ? ' front' : ''}`}
            style={{ transitionDuration: `${profile.crossfade_ms}ms`, transitionTimingFunction: profile.easing }}
            poster={poster}
            playsInline
            muted
            preload="auto"
          />
        ))}
        <span className="arc-video-name">Arcturian</span>
        <span className="arc-video-action" aria-hidden>›</span>
        {caption && (
          <span className={`arc-video-status${startFailed ? ' failed' : ''}`}>{caption}</span>
        )}
      </span>
    </button>
    {controls}
    </div>
  );
}

/**
 * Container: verbindet das Panel mit der laufenden Sprachsitzung.
 *
 * Bewusst getrennt vom Panel — das Panel bleibt rein darstellend und damit
 * ohne Provider testbar. MUSS innerhalb des VoiceProvider gerendert werden
 * (`useVoice()` wirft sonst); der einzige Einhängepunkt ist die Sidebar.
 */
const HIDE_KEY = 'cloud-arcturian-hidden';

export function ArcturianDock({ focus }: { focus: string | null }) {
  const voice = useVoice();
  const pttHeldRef = useRef(false);
  const audioSettingsRef = useRef<HTMLDivElement | null>(null);
  const [audioSettingsOpen, setAudioSettingsOpen] = useState(false);
  // Ausblendbar (Alex 2026-08-05: „kann ich in den Settings deaktivieren, dass
  // ich das Video sehe?"). Standard: sichtbar.
  // STANDARD: AUS (Alex 2026-08-05: „default-mäßig muss der Avatar aus sein").
  // Nur ein ausdrückliches '0' blendet ihn ein — fehlt der Wert, bleibt er weg.
  // Vorher war es umgekehrt, und ein grosses Video erschien ungefragt.
  // Modus lokal spiegeln, damit die Auswahl sofort sichtbar ist. Quelle bleibt
  // localStorage — dieselbe wie im Startdialog und in den Einstellungen.
  const [mode, setModeState] = useState(() => {
    try {
      return localStorage.getItem('cloud-voice-mode') || 'narrator';
    } catch { return 'narrator'; }
  });
  const setMode = useCallback((m: string) => {
    setModeState(m);
    try {
      localStorage.setItem('cloud-voice-mode', m);
      // Der Mint liest weiterhin das alte Flag — synchron halten, sonst mintet
      // die nächste Sitzung mit einem anderen Modus als hier angezeigt.
      localStorage.setItem('cloud-voice-talkback', m === 'talkback' ? 'on' : 'off');
    } catch { /* private mode */ }
    window.dispatchEvent(new CustomEvent('cloud-voice-talkback-change'));
  }, []);
  const [hint, setHint] = useState('');
  useEffect(() => { if (!hint) return; const t = setTimeout(() => setHint(''), 4000); return () => clearTimeout(t); }, [hint]);
  useEffect(() => {
    if (!audioSettingsOpen) return;
    const closeOutside = (event: PointerEvent) => {
      if (!audioSettingsRef.current?.contains(event.target as Node)) setAudioSettingsOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setAudioSettingsOpen(false);
    };
    window.addEventListener('pointerdown', closeOutside);
    window.addEventListener('keydown', closeOnEscape);
    return () => {
      window.removeEventListener('pointerdown', closeOutside);
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [audioSettingsOpen]);
  const [hidden, setHidden] = useState(() => {
    try { return localStorage.getItem(HIDE_KEY) !== '0'; } catch { return true; }
  });
  useEffect(() => {
    const onChange = () => {
      try { setHidden(localStorage.getItem(HIDE_KEY) === '1'); } catch { /* ignore */ }
    };
    window.addEventListener('cloud-arcturian-visibility-change', onChange);
    return () => window.removeEventListener('cloud-arcturian-visibility-change', onChange);
  }, []);
  // NUR die Sichtbarkeits-Einstellung entscheidet über die Anzeige.
  //
  // Vorher hing sie zusätzlich an `canUseVoice` — und genau das hat Alex den
  // Avatar „egal wie die Einstellung ist" verschwinden lassen: meldet der
  // Server für diesen Zugang kein Realtime, war das Feld weg, obwohl der
  // Schalter auf AN stand. Ein Schalter, der nichts bewirkt und nicht sagt
  // warum, ist schlimmer als eine fehlende Funktion.
  //
  // Sachlich ist die Kopplung ohnehin falsch: das Video kommt aus dem Storage
  // und braucht kein Realtime. Nur das GESPRÄCH braucht es — deshalb hängt
  // jetzt der Klick daran, nicht die Darstellung.
  const live = voice.state === 'live';

  // MUSS oberhalb jeder vorzeitigen Rückgabe stehen. Stand er darunter, änderte
  // sich die Zahl der Hooks in genau dem Moment, in dem Alex den Avatar aus-
  // oder einblendet — React bricht das mit Fehler #310 ab, und die Oberfläche
  // stirbt an dieser Stelle. Gemeldet aus seiner Konsole: ArcturianPanel.tsx:272.
  //
  // Dieselbe Klasse wie #966 heute früh: eine Bedingung oberhalb von etwas, das
  // immer laufen muss. Dort war es der Sprachanbieter, hier ein Hook.
  //
  // Sachlich: Ein Druck kann beginnen, bevor der Kanal offen ist. Wir merken
  // uns das physische Halten und hängen das Mikrofon an, sobald die Sitzung
  // live wird — aber nur, wenn der Finger noch unten ist. Frühes Loslassen
  // bleibt damit sicher stumm.
  useEffect(() => {
    voice.pushToTalk(live && pttHeldRef.current);
  }, [live, voice.pushToTalk]);

  if (hidden) return null;

  const connecting = voice.state === 'connecting';
  const failed = voice.state === 'error';

  // Zustand aus der Sitzung ableiten. Bewusst grob: 'thinking' beim Verbinden,
  // 'talking' solange die Stimme läuft, sonst 'listening' im Gespräch. Die
  // feine Abstufung (talk_short/medium/long, happy/sad) kommt aus dem
  // Affect-Kanal, sobald die Arcturian-Lane freigegeben ist — bis dahin wird
  // hier NICHT geraten.
  const state: ArcturianState = connecting ? 'thinking' : live ? 'listening' : 'idle';
  const busy = live || connecting;
  const talking = live && !voice.inputMuted;

  const beginPushToTalk = () => {
    if (!voice.canUseVoice) {
      pttHeldRef.current = false;
      window.alert('Sprachfunktion ist für diesen Zugang nicht freigeschaltet (Realtime fehlt in deinem Profil).');
      return;
    }
    if (!focus) {
      pttHeldRef.current = false;
      setHint('Erst einen Agenten öffnen — dann gedrückt halten');
      return;
    }
    pttHeldRef.current = true;
    setHint('');
    if (live) {
      voice.pushToTalk(true);
    } else if (!connecting) {
      void voice.start(focus);
    }
  };

  const endPushToTalk = () => {
    pttHeldRef.current = false;
    voice.pushToTalk(false);
  };

  const controls = (
    <>
    <div className="arc-controls">
      {/* Modus: wirkt auf die NÄCHSTE Sitzung. Der Modus wird beim
          Verbindungsaufbau festgelegt (er bestimmt, welche Werkzeuge der
          Server überhaupt mintet) — mitten im Gespräch umschalten ginge nur
          über einen Neustart. Deshalb während der Sitzung gesperrt statt
          heimlich wirkungslos. */}
      <div className="arc-modes" role="group" aria-label="Sprachmodus">
        {/* Klartext statt Kürzel (Alex 2026-08-07: „ich weiß auch nicht, wie
            sich Dialog von A unterscheidet"). NARR/TALK/DIALOG/ARC waren für
            mich lesbar und für sonst niemanden. */}
        {([
          ['narrator', 'Zuhören'],
          ['talkback', 'Diktat'],
          ['transparent', 'Als Agent'],
          ['arcturian', 'Arcturian'],
        ] as const).map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={`arc-mode${mode === key ? ' active' : ''}`}
            disabled={busy}
            title={busy
              ? 'Der Modus wird beim Verbinden festgelegt — Wechsel gilt ab dem nächsten Gespräch'
              : key === 'narrator' ? 'Zuhören — erzählt dir, was die Agenten tun. Kann nichts auslösen.'
              : key === 'talkback' ? 'Diktat — du sagst etwas, es wird als Vorschlag an den Agenten formuliert. Du bestätigst jeden einzeln.'
              : key === 'transparent' ? 'Als Agent — die Stimme spricht in der Ich-Form als der gewählte Agent.'
              : 'Arcturian — deine persönliche Sprach-KI; führt freigegebene Agenten-Aufträge aus und antwortet mit geprüftem Receipt.'}
            onClick={() => setMode(key)}
          >
            {label}
          </button>
        ))}
      </div>
      {/* Kein Mute-Toggle mehr: das Video selbst ist der Push-to-talk-Taster.
          Der Pegel beweist weiterhin, ob während des Haltens Audio ankommt. */}
      <MicLevelMeter stream={voice.micStream} active={busy && !voice.inputMuted} />
      {busy && voice.runningModeLabel === 'Arcturian' && (
        <span
          className={`arc-context-status${voice.arcturianInforming ? ' on' : ''}`}
          title={voice.arcturianInforming
            ? 'Kontext wird eingespeist; Arcturian informiert über neue Updates des fokussierten Agenten.'
            : 'Kein laufender Agenten-Kontext wird eingespeist oder erneut berechnet.'}
        >
          {voice.arcturianInforming ? 'Updates an' : 'Updates aus'}
        </span>
      )}
      <div
        className="arc-audio-settings"
        ref={audioSettingsRef}
        // Today this is a sibling of the PTT video button, so events cannot
        // reach it. Keep the boundary explicit as defence if the controls are
        // ever moved back inside a shared clickable shell.
        onPointerDown={(event) => event.stopPropagation()}
        onPointerUp={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className={`arc-settings-btn${audioSettingsOpen ? ' active' : ''}${voice.audioInputSwitching ? ' switching' : ''}${voice.audioInputError ? ' failed' : ''}`}
          aria-expanded={audioSettingsOpen}
          aria-haspopup="dialog"
          aria-label={voice.audioInputError ? `Audio-Einstellungen: ${voice.audioInputError}` : 'Audio-Einstellungen öffnen'}
          title={voice.audioInputError || (voice.audioInputSwitching ? 'Mikrofon wird gewechselt…' : `Audio-Einstellungen · ${voice.activeAudioInputLabel || 'Systemstandard'}`)}
          onClick={() => setAudioSettingsOpen((open) => !open)}
        >
          <Settings size={12} aria-hidden />
        </button>
        {audioSettingsOpen && (
          <div className="arc-audio-popover" role="dialog" aria-label="Audio-Einstellungen">
            <span className="arc-audio-popover-title">Mikrofon</span>
            <AudioInputPicker
              devices={voice.audioInputs}
              selectedId={voice.selectedAudioInputId}
              activeLabel={voice.activeAudioInputLabel}
              switching={voice.audioInputSwitching}
              error={voice.audioInputError}
              compact
              disabled={!voice.canUseVoice || connecting}
              onChange={(deviceId) => { void voice.selectAudioInput(deviceId); }}
            />
          </div>
        )}
      </div>
      <button
        type="button"
        className="arc-stop"
        disabled={!busy}
        onClick={() => voice.stop()}
        title={busy ? 'Gespräch beenden' : 'Kein Gespräch aktiv'}
      >
        STOP
      </button>
    </div>
    </>
  );

  return (
    <ArcturianPanel
      state={state}
      startFailed={failed}
      live={live || connecting}
      talking={talking}
      controls={controls}
      caption={failed
        // Fehler standen bisher NUR in der Leiste oben in der Konversation —
        // im Avatar-Feld wurden sie verschluckt. Für Alex sah ein
        // fehlgeschlagener Start deshalb aus wie „passiert nichts" (2026-08-07:
        // „ich höre nichts der startet nicht"). Ein Fehler, den man nicht
        // sieht, ist schlimmer als der Fehler selbst.
        ? `Start fehlgeschlagen: ${voice.error || 'unbekannter Grund'}`
        : connecting
          ? 'Verbindet…'
          : talking
            ? 'Mikrofon aktiv'
        : hint
          ? hint
          : voice.canUseVoice ? undefined : 'Sprache nicht freigeschaltet'}
      onTalkStart={beginPushToTalk}
      onTalkEnd={endPushToTalk}
    />
  );
}
